import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { MobileSidebar, Sidebar } from "@/components/shell/Sidebar";
import { TopBar } from "@/components/shell/TopBar";
import { CommandPalette } from "@/components/shell/CommandPalette";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";

/** Pages get a subtle per-route atmosphere tint. */
function pageAtmosphere(pathname: string): string | null {
  if (pathname === "/") return "atmosphere-nebula";
  if (pathname.startsWith("/programs")) return "atmosphere-violet";
  return null;
}

export function AppLayout() {
  const location = useLocation();
  const atmosphereClass = pageAtmosphere(location.pathname);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Global ⌘K / Ctrl-K toggle.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((s) => !s);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen flex bg-[var(--color-void)]">
      <Sidebar />

      <div className="relative flex-1 flex flex-col min-w-0">
        {atmosphereClass && (
          <div
            aria-hidden
            className={`pointer-events-none absolute inset-x-0 top-0 h-[640px] opacity-30 ${atmosphereClass}`}
          />
        )}

        <TopBar
          onOpenCommandPalette={() => setPaletteOpen(true)}
          onOpenMobileNav={() => setMobileNavOpen(true)}
        />

        <main className="relative flex-1 px-6 py-8 md:px-10 md:py-10">
          <div className="mx-auto w-full max-w-[1200px]">
            {/* Per-route boundary — keyed by pathname so navigating away
                resets the boundary (one bad page doesn't poison the next). */}
            <ErrorBoundary scope="Route" key={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />

      <MobileSidebar
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />
    </div>
  );
}
